import { useLayoutEffect, useRef } from 'react';

import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { PlannerNoDataState, REM_TO_PX, TOOLTIP_DELAY_DURATION_MS } from '@/modules/planner';
import { AssignmentModal } from '@/modules/planner/components/modals/assignment-modal/assignment-modal';

import { AssignmentActionsProvider } from '../contexts/assignment-actions-context';
import { calculateCellWidth } from '../helpers';
import { getPlannerViewState } from '../helpers/planner-base/get-planner-view-state';
import { usePlannerDerivedRows, usePlannerScrollManager, useZoom } from '../hooks';
import usePlannerStore from '../store';
import { AssignmentType, RowItem } from '../types';
import { FilterRangeEdgeIndicator } from './filter-range/filter-range-edge-indicator';
import { DeleteAssignmentModal } from './modals/delete-assignment-modal';
import { OverlapWarningModal } from './modals/overlap-warning-modal';
import { TimeoffOverlapConfirmationModal } from './modals/timeoff-overlap-confirmation-modal';
import { UnavailableWarningModal } from './modals/unavailable-warning-modal';
import { PlannerEmptyState } from './states/planner-empty-state';
import { PlannerErrorState } from './states/planner-error-state';
import { PlannerSkeleton, PlannerSkeletonRows } from './states/planner-skeleton';
import { TimelineHeader } from './timeline/timeline-header';
import { VirtualizedRows } from './timeline/virtualized-rows';
import { PlannerTooltipProvider } from './tooltip/planner-tooltip-provider';

interface PlannerProps {
  disableZoom?: boolean;
  rows: RowItem[];
  assignments: Map<string, AssignmentType[]>;
  filters: Record<string, string | string[]>;
  onLoadDatesLeft?: () => Promise<void>;
  onLoadDatesRight?: () => Promise<void>;
  onLoadMoreRows: () => void;
  hasMoreRows: boolean;
  isLoadingMoreRows: boolean;
  isLoading?: boolean;
  /**
   * Previous data is shown while changed filters/preferences refetch.
   * The planner stays rendered but dims with a pulse and blocks interaction —
   * the skeleton is reserved for genuinely empty first loads.
   */
  isRefreshing?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
}

export const Planner = ({
  disableZoom = false,
  rows,
  assignments: assignmentsMap,
  filters,
  onLoadDatesLeft,
  onLoadDatesRight,
  onLoadMoreRows,
  hasMoreRows = false,
  isLoadingMoreRows = false,
  isLoading = false,
  isRefreshing = false,
  error = null,
  onRefresh,
}: PlannerProps) => {
  const { t } = useTranslation(['authenticated.planner']);
  const { handleWheel, zoomAnchorRef } = useZoom(disableZoom);
  const {
    zoomLevel,
    editAssignmentData,
    resetEditAssignment,
    showOverlapWarning,
    setShowOverlapWarning,
    showUnavailableWarning,
    setShowUnavailableWarning,
    deleteAssignmentData,
    setDeleteAssignmentData,
    isUiTransitioning,
  } = usePlannerStore(
    useShallow(store => ({
      zoomLevel: store.zoomLevel,
      editAssignmentData: store.editAssignmentData,
      resetEditAssignment: store.resetEditAssignment,
      showOverlapWarning: store.showOverlapWarning,
      setShowOverlapWarning: store.setShowOverlapWarning,
      showUnavailableWarning: store.showUnavailableWarning,
      setShowUnavailableWarning: store.setShowUnavailableWarning,
      deleteAssignmentData: store.deleteAssignmentData,
      setDeleteAssignmentData: store.setDeleteAssignmentData,
      isUiTransitioning: store.uiTransitionCount > 0,
    }))
  );

  const { visibleRows, rowHeights } = usePlannerDerivedRows({
    rows,
    assignmentsMap,
  });

  const cellWidth = calculateCellWidth(zoomLevel);
  const cellWidthPx = cellWidth * REM_TO_PX;

  const { dates, parentRef, shouldShowSkeletonRows } = usePlannerScrollManager({
    rowsLength: rows.length,
    visibleRowsLength: visibleRows.length,
    cellWidthPx,
    isLoading,
    // Needed by the filter-change scroll restore: with keepPreviousData a filter
    // reload never flips isLoading (isPending) — only isRefreshing (placeholder).
    isRefreshing,
    onLoadDatesLeft,
    onLoadDatesRight,
    onLoadMoreRows,
    hasMoreRows,
    isLoadingMoreRows,
  });

  // Keep content horizontally pinned when the cell width changes (zoom). scrollLeft
  // is in px = dateIndex * cellWidthPx; if cellWidthPx changes but scrollLeft doesn't,
  // the same px maps to a different date, so the planner appears to jump. We run this
  // single rescale in a layout effect (before paint), AFTER the new width is laid out.
  //
  // Anchor point:
  // - Wheel/pinch zoom records the cursor position (zoomAnchorRef) → pin that point,
  //   so zooming feels centered under the cursor.
  // - Button/keyboard zoom leave the ref null → pin the left edge (anchorX = 0).
  //
  // Only scrollLeft is touched: row heights do NOT scale with zoom, so adjusting
  // scrollTop would drift the view vertically.
  const prevCellWidthPxRef = useRef(cellWidthPx);
  useLayoutEffect(() => {
    const container = parentRef.current;
    const prevCellWidthPx = prevCellWidthPxRef.current;
    prevCellWidthPxRef.current = cellWidthPx;

    const anchor = zoomAnchorRef.current;
    zoomAnchorRef.current = null;

    if (!container || prevCellWidthPx === cellWidthPx || prevCellWidthPx <= 0) {
      return;
    }

    const ratio = cellWidthPx / prevCellWidthPx;
    const anchorX = anchor?.x ?? 0;
    // Keep the content point currently under anchorX fixed across the width change.
    container.scrollLeft = (container.scrollLeft + anchorX) * ratio - anchorX;
  }, [cellWidthPx, parentRef, zoomAnchorRef]);

  const {
    showInitialLoader,
    showErrorState,
    showEmptyState,
    showNoDataState,
    showPlannerEmptyState,
  } = getPlannerViewState({
    rowCount: rows.length,
    assignmentGroupCount: assignmentsMap.size,
    filters,
    // A refresh with NO previous rows has nothing to dim-pulse (the previous
    // result was empty) — treat it as loading so the skeleton shows instead
    // of a bare empty state.
    isLoading: isLoading || isRefreshing,
    error,
  });

  if (showInitialLoader) {
    return <PlannerSkeleton />;
  }

  return (
    <TooltipProvider skipDelayDuration={0} delayDuration={TOOLTIP_DELAY_DURATION_MS}>
      <PlannerTooltipProvider>
        <AssignmentActionsProvider>
          <div className="relative h-full w-full max-w-full" onWheel={handleWheel}>
            <div
              className={cn(
                'absolute inset-0 h-full overflow-x-auto overflow-y-auto pb-12',
                // Refresh after a filter/preference change: keep the previous content
                // visible but dimmed-pulsing and non-interactive until fresh data lands
                isRefreshing &&
                  'pointer-events-none select-none animate-pulse [animation-duration:1000ms]'
              )}
              id="planner-container"
              style={{ willChange: 'scroll-position', overscrollBehaviorX: 'none' }}
              ref={parentRef}
            >
              <TimelineHeader />

              {!showEmptyState && (
                <VirtualizedRows
                  parentRef={parentRef}
                  allRows={rows}
                  visibleRows={visibleRows}
                  rowHeights={rowHeights}
                />
              )}

              {shouldShowSkeletonRows && (
                <PlannerSkeletonRows
                  rowCount={6}
                  cellWidth={cellWidth}
                  dayCount={dates.length}
                  scrollLeft={parentRef.current?.scrollLeft ?? 0}
                />
              )}
            </div>

            {/* Arrows pointing to the filter range bar when it is scrolled out of view */}
            <FilterRangeEdgeIndicator />

            {isUiTransitioning && (
              <div className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-background/25">
                <div className="flex items-center gap-2 rounded-md border bg-background/90 px-3 py-2 text-xs text-muted-foreground shadow-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('updatingView')}
                </div>
              </div>
            )}

            {showErrorState && onRefresh && <PlannerErrorState onRefresh={onRefresh} />}
            {showPlannerEmptyState && <PlannerEmptyState />}
            {showNoDataState && <PlannerNoDataState />}
          </div>

          <AssignmentModal
            open={!!editAssignmentData}
            onClose={resetEditAssignment}
            initialData={editAssignmentData ?? undefined}
          />

          <OverlapWarningModal
            open={showOverlapWarning}
            onClose={() => setShowOverlapWarning(false)}
          />

          <UnavailableWarningModal
            open={showUnavailableWarning}
            onClose={() => setShowUnavailableWarning(false)}
          />

          <DeleteAssignmentModal
            open={!!deleteAssignmentData}
            data={deleteAssignmentData}
            onClose={() => setDeleteAssignmentData(null)}
          />

          <TimeoffOverlapConfirmationModal />
        </AssignmentActionsProvider>
      </PlannerTooltipProvider>
    </TooltipProvider>
  );
};
